// TitleStyleInput.tsx - Custom input for title with styling controls
import React from "react";
import { useInput, InputProps } from "react-admin";
import {
  Box,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
  Typography,
  Paper,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";

interface TitleStyleData {
  text: string;
  fontSize: number;
  fontWeight: "normal" | "bold";
  fontStyle: "normal" | "italic";
}

interface TitleStyleInputProps extends Omit<InputProps, "source"> {
  source: string;
  label?: string;
  helperText?: string;
}

const TitleStyleInput: React.FC<TitleStyleInputProps> = ({
  source,
  label = "Event Title",
  helperText,
  ...rest
}) => {
  const {
    field,
    fieldState: { error },
  } = useInput({
    source,
    ...rest,
  });

  // Parse the value - handle both old string format and new object format
  const parseValue = (value: any): TitleStyleData => {
    if (!value) {
      return {
        text: "",
        fontSize: 32,
        fontWeight: "bold",
        fontStyle: "normal",
      };
    }

    // If it's a string (old format), convert to new format
    if (typeof value === "string") {
      return {
        text: value,
        fontSize: 32,
        fontWeight: "bold",
        fontStyle: "normal",
      };
    }

    // If it's already the new format
    return {
      text: value.text || "",
      fontSize: value.fontSize || 32,
      fontWeight: value.fontWeight || "bold",
      fontStyle: value.fontStyle || "normal",
    };
  };

  const currentValue = parseValue(field.value);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    field.onChange({
      ...currentValue,
      text: event.target.value,
    });
  };

  const handleFontSizeChange = (_event: Event, newValue: number | number[]) => {
    field.onChange({
      ...currentValue,
      fontSize: newValue as number,
    });
  };

  const handleStyleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFormats: string[]
  ) => {
    field.onChange({
      ...currentValue,
      fontWeight: newFormats.includes("bold") ? "bold" : "normal",
      fontStyle: newFormats.includes("italic") ? "italic" : "normal",
    });
  };

  // Get current selected formats for toggle buttons
  const selectedFormats: string[] = [];
  if (currentValue.fontWeight === "bold") selectedFormats.push("bold");
  if (currentValue.fontStyle === "italic") selectedFormats.push("italic");

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2, width: '100%' }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      {/* Text Input */}
      <TextField
        {...field}
        value={currentValue.text}
        onChange={handleTextChange}
        multiline
        rows={2}
        fullWidth
        variant="outlined"
        placeholder="Enter event title..."
        error={!!error}
        helperText={error?.message || helperText}
        sx={{ mb: 2 }}
      />

      {/* Styling Controls */}
      <Box display="flex" gap={3} alignItems="center" flexWrap="wrap">
        {/* Font Size Slider */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="caption" gutterBottom display="block">
            Font Size: {currentValue.fontSize}px
          </Typography>
          <Slider
            value={currentValue.fontSize}
            onChange={handleFontSizeChange}
            min={16}
            max={72}
            step={1}
            marks={[
              { value: 16, label: "16" },
              { value: 32, label: "32" },
              { value: 48, label: "48" },
              { value: 72, label: "72" },
            ]}
            valueLabelDisplay="auto"
          />
        </Box>

        {/* Bold/Italic Toggle */}
        <Box>
          <Typography variant="caption" gutterBottom display="block">
            Text Style
          </Typography>
          <ToggleButtonGroup
            value={selectedFormats}
            onChange={handleStyleChange}
            aria-label="text formatting"
          >
            <ToggleButton value="bold" aria-label="bold">
              <FormatBoldIcon />
            </ToggleButton>
            <ToggleButton value="italic" aria-label="italic">
              <FormatItalicIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
    </Paper>
  );
};

export default TitleStyleInput;